import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  Alert,
  SelectChangeEvent,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Divider,
  IconButton,
  Button,
  Menu,
  MenuItem as MuiMenuItem,
  Checkbox,
  ListItemIcon,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Tooltip from '@mui/material/Tooltip';
import { useTeacher } from '../../context/TeacherContext';
import apiAxiosInstance from "../../utils/axiosInstance";
import aiAxiosInstance from "../../utils/aiAxiosInstance";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import PreviewIcon from '@mui/icons-material/Preview';
import ReactMarkdown from 'react-markdown';
import SaveIcon from '@mui/icons-material/Save';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { TextFieldProps } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ObservationsHistoryDialog from './ObservationsHistoryDialog';
import FeedbackButton from '../Common/FeedbackButton';
import InlineHelp from '../Common/InlineHelp';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import html2pdf from 'html2pdf.js';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import VideoHelpButton from '../Common/VideoHelpButton';

interface Student {
  _id: string;
  firstName: string;
  lastName: string;
}

interface StudentObservation {
  studentId: string;
  observation: string;
}

interface Group {
  _id: string;
  name: string;
  students: Student[];
  type?: string;
}

interface LessonPlan {
  _id: string;
  groups: string[];
  standard: {
    code: string;
    description: string;
  };
  story: {
    title: string;
    content: string;
  };
  sections: {
    warmUp: string;
    introductionAndGuidedPractice: string;
    independentPractice: string;
    checkingComprehension: string;
  };
  createdAt: string;
}

interface LessonViewDialogProps {
  open: boolean;
  onClose: () => void;
  lessonPlan: LessonPlan | null;
}

interface UsedLessonCombo {
  standardCode: string;
  storyTitle: string;
  date: Date;
}

const isDateInPast = (date: Dayjs) => {
  const today = dayjs().startOf('day');
  return date.isBefore(today);
};

const LessonDelivery: React.FC = () => {
  const { teacher } = useTeacher();
  const navigate = useNavigate();
  const [groups, setGroups] = useState<Group[]>([]);
  // Acts as a filter for students and the group used for saving observations
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [lessonPlans, setLessonPlans] = useState<LessonPlan[]>([]);
  const [selectedLesson, setSelectedLesson] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openStudents, setOpenStudents] = useState<{ [key: string]: boolean }>({});
  const [observations, setObservations] = useState<{ [key: string]: string }>({});
  const [improvingObservation, setImprovingObservation] = useState<{ [key: string]: boolean }>({});
  const [lessonViewOpen, setLessonViewOpen] = useState(false);
  const [selectedLessonPlan, setSelectedLessonPlan] = useState<LessonPlan | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [isSavingObservations, setIsSavingObservations] = useState(false);
  const [observationsHistoryOpen, setObservationsHistoryOpen] = useState(false);
  const [observationsHistory, setObservationsHistory] = useState<{ [key: string]: any[] }>({});
  const [deliveredLessonPlan, setDeliveredLessonPlan] = useState<LessonPlan | null>(null);

  // Create Group dialog state
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupStudentIds, setNewGroupStudentIds] = useState<string[]>([]);
  const [isSavingGroup, setIsSavingGroup] = useState(false);

  // Fetch groups and students when component mounts
  useEffect(() => {
    const fetchGroupsAndStudents = async () => {
      try {
        // Fetch all groups
        let groupsData: Group[] = [];
        try {
          const respAll = await apiAxiosInstance.get('/api/groups', {
            params: { teacherId: teacher?._id }
          });
          groupsData = respAll.data || [];
        } catch (e) {
          groupsData = [];
        }

        // Fetch students
        let studentsData: Student[] = [];
        try {
          const studentsResp = await apiAxiosInstance.get('/api/students', {
            params: { teacherId: teacher?._id }
          });
          studentsData = studentsResp.data || [];
        } catch (e) {
          studentsData = [];
        }

        // Fallback: derive students from groups if direct fetch is empty
        if (!studentsData.length && groupsData.length) {
          const map: Record<string, Student> = {} as any;
          groupsData.forEach((g: any) => {
            (g.students || []).forEach((s: any) => {
              const id = typeof s === 'string' ? s : s._id;
              if (!id) return;
              if (!map[id]) {
                map[id] = typeof s === 'string' ? ({ _id: id, firstName: 'Student', lastName: id.slice(-4) } as any) : s;
              }
            });
          });
          studentsData = Object.values(map);
        }

        setGroups(groupsData);
        setStudents(studentsData);
      } catch (error) {
        console.error('Error fetching groups or students:', error);
        setError('Failed to load groups or students');
      }
    };

    if (teacher?._id) {
      fetchGroupsAndStudents();
    }
  }, [teacher]);

  // Load data dependent on selected group/date and fetch all lesson plans unfiltered by group
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Always load all available lesson plans (do not filter by group)
        const lessonPlansResponse = await apiAxiosInstance.get('/api/small-group-lesson-plans');
        setLessonPlans(lessonPlansResponse.data || []);

        // If group filter is selected, load observations for that group/date
        if (selectedGroup && selectedDate) {
          // Load existing observations for selected date and group
          const currentDateObservationsResponse = await apiAxiosInstance.get('/api/student-observations', {
            params: {
              groupId: selectedGroup,
              date: selectedDate.format('YYYY-MM-DD')
            }
          });

          // Pre-populate observation text by student for that date
          const observationsMap = currentDateObservationsResponse.data.observations.reduce((acc: { [key: string]: string }, obs: any) => {
            if (obs.student?._id) acc[obs.student._id] = obs.observation;
            return acc;
          }, {});
          setObservations(observationsMap);

          // If there's an existing observation with a lesson plan, select it
          if (currentDateObservationsResponse.data.lessonPlanId) {
            setSelectedLesson(currentDateObservationsResponse.data.lessonPlanId);
          }

          // For past dates, capture delivered lesson plan for display
          const today = dayjs().startOf('day');
          const selectedDateStart = selectedDate.startOf('day');
          if (selectedDateStart.isBefore(today)) {
            const deliveredId = currentDateObservationsResponse.data.lessonPlanId;
            const deliveredFromResponse = (currentDateObservationsResponse.data.lessonPlans || []).find((p: any) => p._id === deliveredId) || null;
            setDeliveredLessonPlan(deliveredFromResponse);
          } else {
            setDeliveredLessonPlan(null);
          }
        } else {
          setDeliveredLessonPlan(null);
        }

        setError(null);
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedGroup, selectedDate]);

  useEffect(() => {
    if (selectedLesson) {
      const plan = lessonPlans.find(p => p._id === selectedLesson);
      setSelectedLessonPlan(plan || null);
    } else {
      setSelectedLessonPlan(null);
    }
  }, [selectedLesson, lessonPlans]);

  const handleGroupChange = (event: SelectChangeEvent<string>) => {
    const newGroupId = event.target.value;
    setSelectedGroup(newGroupId);

    const group = groups.find(g => g._id === newGroupId);
    if (group) {
      const idsInGroup = (group.students || []).map(s => (s as any)._id || (s as any));
      const isAutoType = (group.type || '').toLowerCase() === 'small group' || (group.type || '').toLowerCase() === 'intervention';

      if (isAutoType) {
        // Auto-select all and open all panels, but keep Select editable for manual changes
        setSelectedStudentIds(idsInGroup);
        setOpenStudents(prev => idsInGroup.reduce((acc, id) => ({ ...acc, [id]: true }), {} as any));
      } else {
        // For other types, clear selection but allow manual picking
        setSelectedStudentIds([]);
        setOpenStudents({});
      }
    } else {
      // No group -> keep previous manual selection but close panels for a clean start
      setOpenStudents({});
    }
  };
  const handleOpenCreateGroup = () => {
    setNewGroupName('');
    setNewGroupStudentIds([]);
    setCreateGroupOpen(true);
  };
  const handleCloseCreateGroup = () => {
    if (isSavingGroup) return;
    setCreateGroupOpen(false);
  };
  const toggleNewGroupStudent = (studentId: string) => {
    setNewGroupStudentIds(prev => prev.includes(studentId)
      ? prev.filter(id => id !== studentId)
      : [...prev, studentId]
    );
  };
  const handleSaveCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      setIsSavingGroup(true);
      // Create group with default type for this page
      const createResp = await apiAxiosInstance.post('/api/groups', {
        name: newGroupName.trim(),
        description: '',
        type: 'Small Group',
      });
      const newGroupId = createResp.data?._id;
      if (newGroupId && newGroupStudentIds.length) {
        await apiAxiosInstance.post(`/api/groups/${newGroupId}/assign-students`, {
          studentIds: newGroupStudentIds,
        });
      }
      // Fetch the updated/new group to ensure full data
      let createdGroup: Group | null = null;
      if (newGroupId) {
        try {
          const gResp = await apiAxiosInstance.get(`/api/groups/${newGroupId}`);
          createdGroup = gResp.data || null;
        } catch (e) {
          createdGroup = null;
        }
      }
      if (createdGroup) {
        setGroups(prev => [...prev, createdGroup!]);
      }
      // Select the new group
      if (newGroupId) {
        setSelectedGroup(newGroupId);
      }
      setCreateGroupOpen(false);
    } catch (e) {
      console.error('Error creating group:', e);
      setError('Failed to create group');
    } finally {
      setIsSavingGroup(false);
    }
  };
  const handleStudentsChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as unknown as string[] | string;
    const nextIds = (typeof value === 'string' ? value.split(',') : value) as string[];
    // Auto-open newly added students, close removed ones
    setOpenStudents(prev => {
      const next: { [key: string]: boolean } = { ...prev };
      const prevSet = new Set(selectedStudentIds);
      const nextSet = new Set(nextIds);
      nextIds.forEach(id => {
        if (!prevSet.has(id)) next[id] = true;
      });
      Array.from(prevSet).forEach(id => {
        if (!nextSet.has(id)) next[id] = false;
      });
      return next;
    });
    setSelectedStudentIds(nextIds);
  };

  const filteredStudents = useMemo(() => {
    if (!selectedGroup) return students;
    const group = groups.find(g => g._id === selectedGroup);
    if (!group) return students;
    const ids = new Set((group.students || []).map(s => (s as any)._id || (s as any)));
    return students.filter(s => ids.has(s._id));
  }, [selectedGroup, students, groups]);

  const handleLessonChange = (event: SelectChangeEvent<string>) => {
    setSelectedLesson(event.target.value);
  };

  const handleStudentClick = (studentId: string) => {
    setOpenStudents(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  };

  const handleObservationChange = (studentId: string, value: string) => {
    setObservations(prev => ({
      ...prev,
      [studentId]: value
    }));
  };

  const handleImproveObservation = async (studentId: string) => {
    const currentObservation = observations[studentId];
    if (!currentObservation || !selectedLessonPlan) return;

    setImprovingObservation(prev => ({ ...prev, [studentId]: true }));
    try {
      const response = await aiAxiosInstance.post('/improve-observation', {
        observation: currentObservation,
        topic: selectedLessonPlan.standard.description
      });

      if (response.data.content) {
        setObservations(prev => ({
          ...prev,
          [studentId]: response.data.content
        }));
      }
    } catch (error) {
      console.error('Error improving observation:', error);
      setError('Failed to improve observation');
    } finally {
      setImprovingObservation(prev => ({ ...prev, [studentId]: false }));
    }
  };

  const handleSaveObservations = async () => {
    if (!selectedGroup || !selectedLesson || !selectedDate) return;

    setIsSavingObservations(true);
    try {
      const observationsToSave = selectedStudentIds
        .map(studentId => ({ studentId, observation: observations[studentId] || '' }))
        .filter(item => item.observation.trim() !== '');

      if (observationsToSave.length === 0) {
        setError('No observations to save');
        return;
      }

      const response = await apiAxiosInstance.post('/api/student-observations', {
        observations: observationsToSave,
        groupId: selectedGroup,
        lessonPlanId: selectedLesson,
        date: selectedDate.format()
      });

      if (response.data.message) {
        setError(null);
      }
    } catch (error) {
      console.error('Error saving observations:', error);
      setError('Failed to save observations');
    } finally {
      setIsSavingObservations(false);
    }
  };

  const hasObservations = () => {
    return Object.values(observations).some(obs => obs.trim() !== '');
  };

  const fetchObservationHistory = async () => {
    if (!selectedGroup) return;

    try {
      const response = await apiAxiosInstance.get('/api/student-observations/history', {
        params: {
          groupId: selectedGroup
        }
      });

      // Organize observations by student
      const observationsByStudent = response.data.observations.reduce((acc: any, obs: any) => {
        if (!acc[obs.student._id]) {
          acc[obs.student._id] = [];
        }
        acc[obs.student._id].push(obs);
        return acc;
      }, {});

      setObservationsHistory(observationsByStudent);
    } catch (error) {
      console.error('Error fetching observation history:', error);
      setError('Failed to load observation history');
    }
  };

  useEffect(() => {
    if (selectedGroup) {
      fetchObservationHistory();
    }
  }, [selectedGroup]);

  if (!teacher) {
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <VideoHelpButton
          url="https://www.youtube.com/watch?v=5dS_mt8Kxpc"
          title="Lesson Delivery"
          buttonLabel="Watch tutorial"
          color="secondary"
          emphasis="solid"
        />
      </Box>
        <Alert severity="error">Please log in to access this page.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Lesson Delivery
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Empty state when no students exist */}
      {!loading && students.length === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            No students found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add students to start using Lesson Delivery. You can add a single student or import a list.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/add-students')}>
            Add Students
          </Button>
        </Paper>
      )}

      {students.length > 0 && (
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Group Filter, Students and Date Selection */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end' }}>
          <FormControl sx={{ flex: 1 }} size="small">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <InputLabel sx={{ position: 'relative', transform: 'none', mb: 0 }}>Filter by Group</InputLabel>
              <InlineHelp content={`
• Optional filter to narrow the student list
• All of your groups are listed here
• Save Observations requires a specific group`} />
              <Tooltip title="Create Group">
                <IconButton size="small" onClick={handleOpenCreateGroup} sx={{ ml: 1 }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Select
              value={selectedGroup}
              onChange={(event) => {
                const value = (event.target as HTMLInputElement).value as string;
                if (value === '__create__') {
                  // Reset select back to previous value and open dialog
                  setTimeout(() => setCreateGroupOpen(true), 0);
                  return;
                }
                handleGroupChange(event as any);
              }}
              label="Filter by Group"
              size="small"
            >
              <MenuItem value="">
                All Groups
              </MenuItem>
              {groups.map((group) => (
                <MenuItem key={group._id} value={group._id}>
                  {group.name}
                </MenuItem>
              ))}
              <Divider />
              <MenuItem value="__create__">
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                Create New Group
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ flex: 1 }} size="small">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <InputLabel sx={{ position: 'relative', transform: 'none', mb: 0 }}>Select Students</InputLabel>
              <InlineHelp content={`
• Choose one or more students to enter observations
• The list is filtered by the selected group if provided
• Sections for entering information appear after selecting students`} />
            </Box>
            <Select
              multiple
              value={selectedStudentIds}
              onChange={handleStudentsChange}
              label="Select Students"
              renderValue={(selected) => `${(selected as string[]).length} selected`}
              size="small"
            >
              {filteredStudents.map((student) => (
                <MenuItem key={student._id} value={student._id}>
                  {student.firstName} {student.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ flex: 1, position: 'relative' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="body2" color="text.primary">Select Date</Typography>
                <InlineHelp content={`
• Past Dates: View previously delivered lessons
• Current Date: Deliver a new lesson
• Future Dates: Plan upcoming lessons
• Each story can only be used once per group`} />
              </Box>
              <DatePicker
                label="Date"
                value={selectedDate}
                onChange={(value) => {
                  if (dayjs.isDayjs(value)) {
                    setSelectedDate(value);
                  }
                }}
                renderInput={(params: TextFieldProps) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Box>
          </LocalizationProvider>
        </Box>

        {/* Lesson Selection (not filtered by group) */}
        {(
          <>
            {isDateInPast(selectedDate) ? (
              deliveredLessonPlan ? (
                <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Delivered Lesson
                    </Typography>
                    <InlineHelp content={`
• This shows the lesson that was delivered on the selected date
• You cannot modify past lessons
• Use this to review previous lesson content and observations`} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" color="primary.main" gutterBottom>
                      {deliveredLessonPlan.standard?.code}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      {deliveredLessonPlan.standard?.description}
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      Story: {deliveredLessonPlan.story?.title}
                    </Typography>
                  </Box>
                </Paper>
              ) : (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  No lesson was delivered on this date
                </Typography>
              )
            ) : (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <InputLabel sx={{ position: 'relative', transform: 'none', mb: 0 }}>Select Lesson</InputLabel>
                  <InlineHelp content={`
• Choose a lesson plan to deliver
• Each lesson includes:
  - Standard and description
  - Story title and content
  - Lesson sections (warm-up, practice, etc.)
• A story can only be used once per group`} />
                </Box>
                <Select
                  value={selectedLesson}
                  onChange={handleLessonChange}
                  label="Select Lesson"
                  size="small"
                >
                  {lessonPlans.length > 0 ? (
                    lessonPlans.map((plan) => (
                      <MenuItem 
                        key={plan._id} 
                        value={plan._id}
                        sx={{ 
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          py: 1,
                          whiteSpace: 'normal'
                        }}
                      >
                        <Box sx={{ width: '100%' }}>
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              fontWeight: 'bold',
                              color: 'primary.main',
                              mb: 0.5
                            }}
                          >
                            {plan.standard?.code}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              whiteSpace: 'normal',
                              lineHeight: 1.3,
                              mb: 0.5
                            }}
                          >
                            {plan.standard?.description}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontStyle: 'italic',
                              color: 'text.primary'
                            }}
                          >
                            Story: {plan.story?.title}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        No lesson plans available
                      </Typography>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
          </>
        )}

        {selectedLesson && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={() => setLessonViewOpen(true)}
                sx={{ mb: 2 }}
                size="small"
              >
                View Lesson Plan
              </Button>
              <InlineHelp content={`
• View the complete lesson plan details
• Includes:
  - Story text
  - Warm-up activities
  - Guided practice
  - Independent practice
  - Comprehension questions`} />
            </Box>
          </Box>
        )}

        {/* Selected Students List - Compact Layout */}
        {selectedStudentIds.length > 0 && (
          <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
            <List
              dense
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                columnGap: 1.5,
                rowGap: 1.5,
                p: 0,
              }}
            >
              {selectedStudentIds.map((studentId) => {
                const student = students.find(s => s._id === studentId);
                if (!student) return null;
                return (
                  <ListItem
                    key={student._id}
                    disablePadding
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <ListItemButton
                      onClick={() => handleStudentClick(student._id)}
                      sx={{
                        px: 1,
                        py: 0.5,
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <ListItemText
                        primary={`${student.firstName} ${student.lastName}`}
                        sx={{ '& .MuiListItemText-primary': { fontWeight: 'medium' } }}
                      />
                      {openStudents[student._id] ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={openStudents[student._id]} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 1, backgroundColor: 'background.paper', position: 'relative' }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Observations"
                          value={observations[student._id] || ''}
                          onChange={(e) => handleObservationChange(student._id, e.target.value)}
                          variant="outlined"
                          size="small"
                          sx={{ mt: 0.5 }}
                          disabled={isDateInPast(selectedDate)}
                        />
                        {!isDateInPast(selectedDate) && (
                          <Tooltip title="Improve with AI">
                            <IconButton
                              onClick={() => handleImproveObservation(student._id)}
                              disabled={!observations[student._id] || improvingObservation[student._id]}
                              sx={{
                                position: 'absolute',
                                right: 12,
                                top: 12,
                                backgroundColor: 'primary.main',
                                color: 'white',
                                '&:hover': { 
                                  backgroundColor: '#ff9800 !important',
                                  transform: 'scale(1.05)',
                                },
                                '&.Mui-disabled': {
                                  backgroundColor: 'action.disabledBackground',
                                  color: 'action.disabled',
                                },
                                width: 32,
                                height: 32,
                                zIndex: 1,
                                transition: 'all 0.2s ease-in-out',
                              }}
                            >
                              {improvingObservation[student._id] ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <AutoFixHighIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Collapse>
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        )}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          {!isDateInPast(selectedDate) && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => setObservationsHistoryOpen(true)}
                  startIcon={<HistoryIcon />}
                  disabled={!selectedGroup}
                  size="small"
                >
                  View History
                </Button>
                <InlineHelp content={`
• View past observations for all students
• Track progress over time
• See previous lesson details
• Review teaching notes and strategies`} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveObservations}
                  disabled={!selectedGroup || !selectedLesson || !hasObservations() || isSavingObservations}
                  startIcon={isSavingObservations ? <CircularProgress size={20} /> : <SaveIcon />}
                  size="small"
                >
                  {isSavingObservations ? 'Saving...' : 'Save Observations'}
                </Button>
                <InlineHelp content={`
• Save all student observations
• Observations are saved with:
  - Selected lesson plan
  - Current date
  - Group context (select a group to enable saving)
• Button is disabled until you add observations`} />
              </Box>
            </>
          )}
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" mt={3}>
            <CircularProgress />
          </Box>
        )}
      </Paper>
      )}

      <LessonViewDialog 
        open={lessonViewOpen}
        onClose={() => setLessonViewOpen(false)}
        lessonPlan={selectedLessonPlan}
      />

      <ObservationsHistoryDialog
        open={observationsHistoryOpen}
        onClose={() => setObservationsHistoryOpen(false)}
        observations={observationsHistory}
      />

      {/* Create Group Dialog */}
      <Dialog open={createGroupOpen} onClose={handleCloseCreateGroup} maxWidth="sm" fullWidth>
        <DialogTitle>Create Group</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Group Name"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              fullWidth
              autoFocus
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Students</Typography>
              <Paper variant="outlined" sx={{ maxHeight: 260, overflow: 'auto', pr: 1 }}>
                <List dense sx={{ p: 0 }}>
                  {students.map((s) => (
                    <ListItem key={s._id} disablePadding sx={{ pr: 7 }} secondaryAction={
                      <Checkbox
                        edge="end"
                        checked={newGroupStudentIds.includes(s._id)}
                        onChange={() => toggleNewGroupStudent(s._id)}
                        inputProps={{ 'aria-label': `select ${s.firstName} ${s.lastName}` }}
                      />
                    }>
                      <ListItemButton onClick={() => toggleNewGroupStudent(s._id)} sx={{ py: 1, pl: 2, pr: 2 }}>
                        <ListItemText primary={`${s.firstName} ${s.lastName}`} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateGroup} disabled={isSavingGroup}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCreateGroup} disabled={isSavingGroup || !newGroupName.trim()}>
            {isSavingGroup ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <FeedbackButton page="lesson-delivery" />
    </Box>
  );
};

const LessonViewDialog: React.FC<LessonViewDialogProps> = ({ open, onClose, lessonPlan }) => {
  // State for download menu
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(null);

  if (!lessonPlan) return null;

  /**
   * Formats markdown content by removing markdown syntax for clean document output
   * @param content - The markdown content to format
   * @returns Formatted content without markdown syntax
   */
  const formatMarkdownContent = (content: string) => {
    return content
      .replace(/\*\*/g, '') // Remove bold markers
      .replace(/\*/g, '')   // Remove italic markers
      .replace(/#{1,6}\s/g, '') // Remove heading markers
      .replace(/`/g, '')    // Remove code markers
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Replace links with just text
      .replace(/\n\n/g, '\\par\\par ') // Convert double newlines to RTF paragraphs
      .replace(/\n/g, '\\line ') // Convert single newlines to RTF line breaks
      .replace(/\s+/g, ' ') // Normalize spaces
      .trim();
  };

  /**
   * Formats content for HTML output by converting markdown to HTML
   * @param content - The markdown content to format
   * @returns HTML formatted content
   */
  const formatHtmlContent = (content: string) => {
    return content
      .replace(/\*\*/g, '') // Remove bold markers
      .replace(/\*/g, '')   // Remove italic markers
      .replace(/#{1,6}\s/g, '') // Remove heading markers
      .replace(/`/g, '')    // Remove code markers
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Replace links with just text
      .replace(/\n\n/g, '</p><p>') // Convert double newlines to paragraphs
      .replace(/\n/g, '<br>') // Convert single newlines to line breaks
      .trim();
  };

  /**
   * Handles opening the download menu
   * @param event - The click event
   */
  const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  /**
   * Handles closing the download menu
   */
  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  /**
   * Generates and downloads the lesson plan as a PDF file
   * Uses html2pdf library to convert HTML content to PDF
   */
  const handleDownloadPDF = async () => {
    handleDownloadClose();
    const element = document.createElement('div');
    element.innerHTML = `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
        <style>
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            /* Enhanced page break control */
            .section {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              display: block;
              position: relative;
              margin: 20px 0;
              padding: 15px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              background-color: #fafafa;
              min-height: 100px;
            }
            
            h1 {
              font-size: 24px !important;
              font-weight: bold !important;
              color: #1976d2 !important;
              text-align: center !important;
              margin: 0 0 25px 0 !important;
              padding: 10px 0 !important;
              border-bottom: 2px solid #1976d2 !important;
              page-break-after: avoid !important;
              break-after: avoid !important;
            }
            
            h2 {
              page-break-after: avoid !important;
              break-after: avoid !important;
              font-size: 18px !important;
              font-weight: bold !important;
              color: #1976d2 !important;
              margin: 0 0 15px 0 !important;
              padding: 8px 12px !important;
              background-color: #e3f2fd !important;
              border-left: 4px solid #1976d2 !important;
              border-radius: 4px !important;
              orphans: 2;
              widows: 2;
            }
            
            .content-wrapper {
              page-break-before: avoid !important;
              break-before: avoid !important;
              orphans: 3;
              widows: 3;
            }
            
            .content {
              page-break-before: avoid !important;
              break-before: avoid !important;
              white-space: pre-wrap;
              word-wrap: break-word;
              line-height: 1.6;
              orphans: 4;
              widows: 4;
            }
            
            /* Enhanced paragraph control */
            p {
              orphans: 4 !important;
              widows: 4 !important;
              margin: 0 0 12px 0;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              line-height: 1.8 !important;
            }
            
            /* Enhanced div control for poetry and drama */
            div {
              orphans: 3;
              widows: 3;
            }
            
            /* Strong tags for character names */
            strong {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            /* List control */
            ul, ol {
              orphans: 2;
              widows: 2;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            li {
              orphans: 2;
              widows: 2;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
          }
        </style>
        <h1 style="color: #1976d2; margin: 0 0 20px 0;">Lesson Plan: ${lessonPlan.story.title}</h1>
        ${lessonPlan.standard.code ? `
          <div style="margin: 0 0 20px 0;">
            <strong>Standard:</strong> ${lessonPlan.standard.code}
            ${lessonPlan.standard.description ? `<br><em>${lessonPlan.standard.description}</em>` : ''}
          </div>
        ` : ''}

        <div class="section">
          <h2 style="color: #1976d2;">Story: ${lessonPlan.story.title}</h2>
          <div class="content-wrapper">
            <div class="content" style="background-color: #f5f5f5; padding: 15px; border-radius: 4px;">
              ${lessonPlan.story.content}
            </div>
          </div>
        </div>

        <div class="section">
          <h2 style="color: #1976d2;">Warm Up</h2>
          <div class="content-wrapper">
            <div class="content">${formatHtmlContent(lessonPlan.sections.warmUp)}</div>
          </div>
        </div>

        <div class="section">
          <h2 style="color: #1976d2;">Introduction and Guided Practice</h2>
          <div class="content-wrapper">
            <div class="content">${formatHtmlContent(lessonPlan.sections.introductionAndGuidedPractice)}</div>
          </div>
        </div>

        <div class="section">
          <h2 style="color: #1976d2;">Independent Practice</h2>
          <div class="content-wrapper">
            <div class="content">${formatHtmlContent(lessonPlan.sections.independentPractice)}</div>
          </div>
        </div>

        <div class="section">
          <h2 style="color: #1976d2;">Checking Comprehension</h2>
          <div class="content-wrapper">
            <div class="content">${formatHtmlContent(lessonPlan.sections.checkingComprehension)}</div>
          </div>
        </div>
      </div>
    `;

    const opt = {
      margin: 0.75,
      filename: `lesson-plan-${lessonPlan.story.title}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        letterRendering: true
      },
      jsPDF: { 
        unit: 'in', 
        format: 'letter', 
        orientation: 'portrait',
        compress: true
      },
      pagebreak: { 
        mode: ['avoid-all'],
        before: '.section',
        avoid: ['h2', '.content-wrapper', '.content']
      }
    };

    try {
      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Could add error handling/notification here
    }
  };

  /**
   * Generates and downloads the lesson plan as a Word document
   * Creates RTF format which is compatible with Microsoft Word
   */
  const handleDownloadWord = () => {
    handleDownloadClose();
    try {
      let rtfContent = `{\\rtf1\\ansi\\deff0
{\\fonttbl{\\f0\\fnil\\fcharset0 Arial;}}
{\\colortbl;\\red25\\green118\\blue210;}

\\paperw12240\\paperh15840\\margl1440\\margr1440\\margt1440\\margb1440
\\widowctrl\\ftnbj\\aenddoc\\wrapdefault\\adjustright

\\pard\\qc\\b\\fs36 Lesson Plan: ${lessonPlan.story.title}\\par
${lessonPlan.standard.code ? `\\pard\\sa360\\fs24\\b Standard: ${lessonPlan.standard.code}\\par` : ''}
${lessonPlan.standard.description ? `\\pard\\sa360\\fs24\\i ${lessonPlan.standard.description}\\i0\\par` : ''}

\\pard\\qc\\b\\fs32 Story: ${lessonPlan.story.title}\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${lessonPlan.story.content}\\par

\\pard\\qc\\b\\fs32 Warm Up\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${formatMarkdownContent(lessonPlan.sections.warmUp)}\\par

\\pard\\qc\\b\\fs32 Introduction and Guided Practice\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${formatMarkdownContent(lessonPlan.sections.introductionAndGuidedPractice)}\\par

\\pard\\qc\\b\\fs32 Independent Practice\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${formatMarkdownContent(lessonPlan.sections.independentPractice)}\\par

\\pard\\qc\\b\\fs32 Checking Comprehension\\par
\\pard\\sa360\\fs24\\b0\\sl480\\slmult1 ${formatMarkdownContent(lessonPlan.sections.checkingComprehension)}\\par
}`;

      const blob = new Blob([rtfContent], { type: 'application/msword' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `lesson-plan-${lessonPlan.story.title}.doc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error generating Word document:', error);
      // Could add error handling/notification here
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Lesson Plan: {lessonPlan.story.title}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {lessonPlan.standard.code} - {lessonPlan.standard.description}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Story Section */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Story: {lessonPlan.story.title}
            </Typography>
            <Box sx={{ 
              maxHeight: '200px', 
              overflow: 'auto',
              backgroundColor: 'grey.50',
              p: 2,
              borderRadius: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'grey.100',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'grey.400',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'grey.500',
                },
              },
            }}>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {lessonPlan.story.content}
              </Typography>
            </Box>
          </Paper>

          {/* Warm Up */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Warm Up
            </Typography>
            <ReactMarkdown>{lessonPlan.sections.warmUp}</ReactMarkdown>
          </Paper>

          {/* Introduction and Guided Practice */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Introduction and Guided Practice
            </Typography>
            <ReactMarkdown>{lessonPlan.sections.introductionAndGuidedPractice}</ReactMarkdown>
          </Paper>

          {/* Independent Practice */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Independent Practice
            </Typography>
            <ReactMarkdown>{lessonPlan.sections.independentPractice}</ReactMarkdown>
          </Paper>

          {/* Checking Comprehension */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Checking Comprehension
            </Typography>
            <ReactMarkdown>{lessonPlan.sections.checkingComprehension}</ReactMarkdown>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadClick}
        >
          Download
        </Button>
        
        {/* Download Menu */}
        <Menu
          anchorEl={downloadAnchorEl}
          open={Boolean(downloadAnchorEl)}
          onClose={handleDownloadClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MuiMenuItem onClick={handleDownloadPDF}>
            <PictureAsPdfIcon sx={{ mr: 1 }} />
            PDF
          </MuiMenuItem>
          <MuiMenuItem onClick={handleDownloadWord}>
            <DescriptionIcon sx={{ mr: 1 }} />
            Word
          </MuiMenuItem>
        </Menu>
      </DialogActions>
    </Dialog>
  );
};

export default LessonDelivery;